import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, hasMinRole } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import DemoCatalog from "@/pages/DemoCatalog";
import DemoLifecycle from "@/pages/DemoLifecycle";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import MyWork from "@/pages/MyWork";
import Insights from "@/pages/Insights";
import InsightCreate from "@/pages/InsightCreate";
import InsightDetail from "@/pages/InsightDetail";
import Meetings from "@/pages/Meetings";
import MeetingWorkspace from "@/pages/MeetingWorkspace";
import MeetingSummary from "@/pages/MeetingSummary";
import AgendaWorkspace from "@/pages/AgendaWorkspace";
import ForumShell from "@/components/layout/ForumShell";
import MyForum from "@/pages/MyForum";
import ForumMeeting from "@/pages/ForumMeeting";
import ForumItem from "@/pages/ForumItem";
import Bills from "@/pages/Bills";
import PublicPortal from "@/pages/PublicPortal";
import BillDetail from "@/pages/BillDetail";
import SearchPage from "@/pages/SearchPage";
import Assignments from "@/pages/Assignments";
import Watchlists from "@/pages/Watchlists";
import Brief from "@/pages/Brief";
import Analytics from "@/pages/Analytics";
import Reports from "@/pages/Reports";
import Alerts from "@/pages/Alerts";
import Hub from "@/pages/Hub";
import UserGuide from "@/pages/UserGuide";
import Admin from "@/pages/Admin";
import Records from "@/pages/Records";
import SsoComplete from "@/pages/SsoComplete";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
}

function Protected({ children, min }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (min && !hasMinRole(user.role, min)) return <Navigate to="/my-work" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/my-work" replace />;
  return children;
}

// Public marketing homepage. Renders immediately (no auth gate); redirects only once a signed-in user is confirmed.
function LandingRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/my-work" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Process Google OAuth callback synchronously during render (avoids race conditions).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/public/*" element={<PublicPortal />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/demo" element={<DemoCatalog />} />
      <Route path="/demo/legislative-lifecycle" element={<DemoLifecycle />} />
      <Route path="/sso-complete" element={<SsoComplete />} />
      <Route element={<Protected><ForumShell /></Protected>}>
        <Route path="/forum" element={<MyForum />} />
        <Route path="/forum/meetings/:id" element={<ForumMeeting />} />
        <Route path="/forum/meetings/:id/items/:itemId" element={<ForumItem />} />
      </Route>
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-work" element={<MyWork />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/new" element={<Protected min="analyst"><InsightCreate /></Protected>} />
        <Route path="/insights/:id" element={<InsightDetail />} />
        <Route path="/meetings" element={<Meetings />} />
        <Route path="/meetings/:id" element={<MeetingWorkspace />} />
        <Route path="/meetings/:id/summary" element={<Protected min="manager"><MeetingSummary /></Protected>} />
        <Route path="/meetings/:id/agenda" element={<Protected min="manager"><AgendaWorkspace /></Protected>} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/bills/:id" element={<BillDetail />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/assignments" element={<Assignments />} />
        <Route path="/watchlists" element={<Watchlists />} />
        <Route path="/brief" element={<Brief />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/hub" element={<Hub />} />
        <Route path="/guide" element={<UserGuide />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/reports" element={<Protected min="analyst"><Reports /></Protected>} />
        <Route path="/records" element={<Records />} />
        <Route path="/admin" element={<Protected min="manager"><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/my-work" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
